/**
 * NightMotes — sparse drifting bioluminescent plankton for dark mode.
 *
 * A single THREE.Points cloud (~40 additive-blended sprites) that drifts
 * slowly upward with a gentle sinusoidal sway. All buffers are allocated
 * once in useMemo; useFrame only mutates the existing position attribute,
 * so there are zero per-frame allocations.
 */
import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const COUNT = 40;
const AREA = 7; // half-extent in x/z, matches diorama footprint
const MIN_Y = 0.3;
const HEIGHT = 4;

export function NightMotes() {
  const pointsRef = useRef<THREE.Points>(null);

  const { positions, basePositions, seeds } = useMemo(() => {
    const positions = new Float32Array(COUNT * 3);
    const seeds = new Float32Array(COUNT * 2); // [phase, riseSpeed]
    for (let i = 0; i < COUNT; i++) {
      positions[i * 3] = (Math.random() * 2 - 1) * AREA;
      positions[i * 3 + 1] = MIN_Y + Math.random() * HEIGHT;
      positions[i * 3 + 2] = (Math.random() * 2 - 1) * AREA;
      seeds[i * 2] = Math.random() * Math.PI * 2;
      seeds[i * 2 + 1] = 0.08 + Math.random() * 0.12;
    }
    return { positions, basePositions: positions.slice(), seeds };
  }, []);

  useFrame(({ clock }, delta) => {
    const points = pointsRef.current;
    if (!points) return;
    const attr = points.geometry.getAttribute("position") as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    const t = clock.elapsedTime;
    for (let i = 0; i < COUNT; i++) {
      const i3 = i * 3;
      const phase = seeds[i * 2];
      const rise = seeds[i * 2 + 1];
      // Slow upward drift, wrapping back to the seabed
      basePositions[i3 + 1] += rise * delta;
      if (basePositions[i3 + 1] > MIN_Y + HEIGHT) basePositions[i3 + 1] = MIN_Y;
      arr[i3] = basePositions[i3] + Math.sin(t * 0.3 + phase) * 0.5;
      arr[i3 + 1] = basePositions[i3 + 1];
      arr[i3 + 2] = basePositions[i3 + 2] + Math.cos(t * 0.25 + phase) * 0.5;
    }
    attr.needsUpdate = true;
  });

  return (
    <points ref={pointsRef} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={positions}
          count={COUNT}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        color="#7FE9FF"
        size={0.07}
        sizeAttenuation
        transparent
        opacity={0.65}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
